import { RACSearchDropdown } from '@rentacenter/racstrap';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  ClassificationsInputPlaceholder,
  emptyField,
  PoFormField,
} from '../../constants/constants';
import { ClassificationType } from '../../domain/PurchaseOrder/ClassificationType';
import { PoStatus } from '../../domain/PurchaseOrder/PoStatus';
import { SelectedFieldOption } from '../../types/types';
import { filterById, populateSelectByChild } from '../../utils/utils';
import {
  useClassificationsDispatch,
  useClassificationsState,
} from './context/Provider';
import { getClassificationFieldName } from './context/fieldNames';
import { ClassificationsComponentProps } from './context/types';

export const bracketComponentTestId = 'bracketComponent';

// eslint-disable-next-line
export const Bracket = (props: ClassificationsComponentProps) => {
  const { name, label, purchaseOrder, qrScannerOutput, showErrorMessage } =
    props;
  const { errors, setValue } = useFormContext();
  const { brackets, startWithDepartment } = useClassificationsState();
  const dispatch = useClassificationsDispatch();
  const [itemValue, setItemValue] = useState(emptyField);
  const [isViewMode, setIsViewMode] = useState(false);
  const getFormFieldName = useCallback(
    (fieldName: PoFormField) => getClassificationFieldName(name, fieldName),
    [name]
  );

  useEffect(
    // eslint-disable-next-line
    () => {
      const poSubDepartment = purchaseOrder && purchaseOrder.subDepartment;
      const poBracket = purchaseOrder && purchaseOrder.bracket;
      const selectedBraket: {
        name: string;
        id: string;
        types: [];
      } | null =
        poSubDepartment && poBracket && filterById(brackets, poBracket)[0];

      if (brackets.length === 1) {
        setItemValue({ value: brackets[0].id, label: brackets[0].name });

        if (startWithDepartment) {
          dispatch({
            type: ClassificationType.SET_SINGLE_TYPE,
            payload: brackets[0].types,
          });
        }
      } else {
        setItemValue({
          value: (selectedBraket && selectedBraket.id) || '',
          label:
            (selectedBraket && selectedBraket.name) ||
            ClassificationsInputPlaceholder,
        });

        if (startWithDepartment) {
          dispatch({
            type: ClassificationType.CLEAR_TYPES,
            payload: [],
          });

          if (selectedBraket) {
            dispatch({
              type: ClassificationType.SET_BRACKETS,
              payload: selectedBraket.types,
            });
          }
        }
      }
      if (qrScannerOutput) {
        const selectedItem = brackets?.find(
          (o) => o.id == qrScannerOutput.bracketNumber
        );
        setItemValue({
          value: selectedItem?.id
            ? selectedItem?.id
            : qrScannerOutput?.bracketNumber?.toString() || '',
          label: selectedItem?.name
            ? selectedItem?.name
            : qrScannerOutput?.bracket
            ? qrScannerOutput?.bracket
            : '',
        });
        setValue(
          getFormFieldName(PoFormField.Bracket),
          selectedItem?.id
            ? selectedItem?.id
            : qrScannerOutput?.bracketNumber?.toString(),
          {
            shouldValidate: true,
            shouldDirty: true,
          }
        );
        setIsViewMode(
          qrScannerOutput?.isScannedCategoryKept
            ? qrScannerOutput?.isScannedCategoryKept
            : false
        );
        if (!qrScannerOutput.departmentNumber) {
          setItemValue(emptyField);
          dispatch({
            type: ClassificationType.CLEAR_SUBTYPES,
          });
        }
        // }
      }
    },
    // eslint-disable-next-line
    [brackets, dispatch, startWithDepartment,qrScannerOutput]
  );

  // Flag if dashboard-sent value is not found in classification options
  useEffect(() => {
    if (qrScannerOutput || !brackets?.length) return;
    const prefillValue = purchaseOrder?.bracket;
    if (!prefillValue) return;
    const resolved = brackets.some((b: any) => b.id == prefillValue);
    setTimeout(
      () => setValue(name + '__unresolved', resolved ? '' : 'true'),
      0
    );
    // eslint-disable-next-line
  }, [brackets, qrScannerOutput]);

  const handleChange = useCallback(
    ({ value, label, types }: SelectedFieldOption) => {
      setItemValue({ value, label });
      setValue(name, value, { shouldValidate: true, shouldDirty: true });

      !startWithDepartment &&
        dispatch({
          type: ClassificationType.SET_BRACKET_END,
        });

      if (startWithDepartment && types) {
        let filteredSubTypes = types[0].subTypes;

        if (purchaseOrder && purchaseOrder.bracket !== value) {
          purchaseOrder.itemType = '';
          purchaseOrder.itemSubType = '';
        }

        dispatch({
          type: ClassificationType.SET_BRACKETS,
          payload: types,
        });

        if (types.length === 1) {
          setValue(getFormFieldName(PoFormField.ItemType), types[0].id, {
            shouldValidate: true,
            shouldDirty: true,
          });
        } else {
          filteredSubTypes = [];
          setValue(getFormFieldName(PoFormField.ItemType), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }

        if (filteredSubTypes && filteredSubTypes.length === 1) {
          setValue(
            getFormFieldName(PoFormField.ItemSubType),
            filteredSubTypes[0].id,
            {
              shouldValidate: true,
              shouldDirty: true,
            }
          );
        } else {
          filteredSubTypes = [];
          setValue(getFormFieldName(PoFormField.ItemSubType), '', {
            shouldValidate: false,
            shouldDirty: true,
          });
        }
      }
    },
    [
      dispatch,
      getFormFieldName,
      name,
      purchaseOrder,
      setValue,
      startWithDepartment,
    ]
  );

  useEffect(
    // eslint-disable-next-line
      () => {
      setIsViewMode(
        purchaseOrder
          ? purchaseOrder?.poStatus === PoStatus.SUBMITTED ||
              purchaseOrder.poStatus === PoStatus.OPEN_CANCELLED
          : false
      );
    },
    [purchaseOrder]
  );

  return useMemo(() => {
    return (
      <Controller
        name={name}
        rules={{ required: true }}
        render={({ name }) => (
          <RACSearchDropdown
            name={name}
            inputLabel={label}
            options={populateSelectByChild(brackets, 'types') || []}
            OnChange={handleChange}
            required
            disabled={isViewMode || !brackets.length}
            value={itemValue}
            errormessage={
              showErrorMessage && get(errors, name)
                ? `${label} is required`
                : ''
            }
          />
        )}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    brackets,
    errors,
    itemValue,
    label,
    name,
    purchaseOrder,
    showErrorMessage,
  ]);
};
